import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/lib/auth/store";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

const AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/sso-url",
  "/auth/sso-callback",
  "/auth/logout",
];

function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return (payload.exp as number) * 1000;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string, marginMs = 60_000): boolean {
  const exp = getTokenExp(token);
  return exp === null || exp < Date.now() + marginMs;
}

let refreshPromise: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const rt = useAuthStore.getState().refreshToken;
  if (!rt) throw new Error("No refresh token");

  const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
    refreshToken: rt,
  });
  const tokens = data?.data;
  if (!tokens?.accessToken || !tokens?.refreshToken)
    throw new Error("Invalid refresh response");

  useAuthStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
  scheduleBackgroundRefresh(tokens.accessToken);
  return tokens.accessToken;
}

async function ensureFreshToken(): Promise<void> {
  const token = useAuthStore.getState().token;
  if (!token || !isTokenExpired(token)) return;

  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  await refreshPromise;
}

// ---------------------------------------------------------------------------
// Background refresh: keeps the session alive even when the user is idle.
// Schedules a refresh at 75% of the access token's remaining lifetime so the
// refresh token never gets a chance to expire from inactivity.
// ---------------------------------------------------------------------------
let backgroundRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearBackgroundRefresh() {
  if (backgroundRefreshTimer !== null) {
    clearTimeout(backgroundRefreshTimer);
    backgroundRefreshTimer = null;
  }
}

function scheduleBackgroundRefresh(accessToken: string) {
  clearBackgroundRefresh();

  const exp = getTokenExp(accessToken);
  if (!exp) return;

  const remaining = exp - Date.now();
  if (remaining <= 0) return;

  const delay = Math.max(remaining * 0.75, 10_000);

  backgroundRefreshTimer = setTimeout(async () => {
    const token = useAuthStore.getState().token;
    if (!token) return;
    try {
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch {
      // Refresh failed — the next user action will trigger logout if needed.
    }
  }, delay);
}

export function startBackgroundRefresh() {
  const token = useAuthStore.getState().token;
  if (token && !isTokenExpired(token, 0)) {
    scheduleBackgroundRefresh(token);
  }
}

export function stopBackgroundRefresh() {
  clearBackgroundRefresh();
}

// Restart the timer when the tab regains focus (laptop sleep, tab switch, …)
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      const token = useAuthStore.getState().token;
      if (token) {
        if (isTokenExpired(token)) {
          // Token already expired — try an immediate refresh
          if (!refreshPromise) {
            refreshPromise = doRefresh()
              .finally(() => { refreshPromise = null; });
            refreshPromise.catch(() => {});
          }
        } else {
          scheduleBackgroundRefresh(token);
        }
      }
    }
  });
}

// Request interceptor: proactive refresh + attach bearer token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) =>
      config.url?.includes(p)
    );

    if (!isAuthEndpoint && useAuthStore.getState().token) {
      try {
        await ensureFreshToken();
      } catch {
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(new Error("Session expirée"));
      }
    }

    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: retry once with a fresh token on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & { _retried?: boolean } | undefined;
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) =>
      config?.url?.includes(p)
    );

    if (
      error.response?.status === 401 &&
      !isAuthEndpoint &&
      config &&
      !config._retried
    ) {
      config._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        config.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(config);
      } catch {
        useAuthStore.getState().logout();
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
      }
    }

    return Promise.reject(error);
  }
);

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;
    if (Array.isArray(data?.detail)) {
      return data.detail.map((d: { msg: string }) => d.msg).join(", ");
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Une erreur est survenue";
}
